"use client";

/**
 * MusicIngredientsLabel — BB's "nutrition label" for a track.
 *
 * Shows, like ingredients on a loaf of bread, exactly how the music was made:
 * AI vs human split, the AI model, creator-declared influences (with sources),
 * live musicians overdubbed, and a "go full nerd" making-of. When AI is involved
 * but credits are missing, it shows an honest warning + why it matters.
 *
 * NOTE (i18n): strings are English-first for v1 — locale backfill tracked as a
 * follow-up per project rules.
 */

import { useState } from "react";
import {
  AIDisclosure, aiLevelMeta, missingDisclosureReasons, AI_CREDITS_EDUCATION,
} from "@/data/ai-disclosure";
import { cn } from "@/lib/utils";
import {
  Wheat, ExternalLink, ChevronDown, ChevronUp, AlertTriangle, Bot, Mic2, Music2,
} from "lucide-react";
import { AiLabelIcon } from "@/components/ui/ai-label-icon";

export function MusicIngredientsLabel({
  disclosure,
  trackTitle,
  className,
}: {
  disclosure?: AIDisclosure | null;
  trackTitle?: string;
  className?: string;
}) {
  const [nerd, setNerd] = useState(false);
  if (!disclosure || !disclosure.level) return null;

  const meta = aiLevelMeta(disclosure.level);
  const aiPct = typeof disclosure.aiPercent === "number" ? Math.max(0, Math.min(100, disclosure.aiPercent)) : null;
  const humanPct = aiPct != null ? 100 - aiPct : null;
  const missing = missingDisclosureReasons(disclosure);
  const hasAI = disclosure.level !== "human";

  return (
    <div className={cn(
      "rounded-xl border-2 border-foreground/80 bg-background overflow-hidden font-mono",
      className
    )}>
      {/* Header — like the top of a bread label */}
      <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-foreground/80 bg-foreground/[0.04]">
        <Wheat className="h-4 w-4 flex-shrink-0" />
        <span className="text-xs font-bold uppercase tracking-widest">Made With — Ingredients</span>
        <span className="ml-auto text-base" title={meta.label}>{meta.emoji}</span>
      </div>

      <div className="px-3 py-3 space-y-3 text-sm">
        {/* Headline level */}
        <div>
          <p className="font-bold text-base leading-tight">{meta.label}</p>
          <p className="text-xs text-muted-foreground">{meta.blurb}</p>
        </div>

        {/* Recording-industry standard track-level AI label (mark + verbatim text) */}
        {hasAI && (meta as any).industryText && (
          <div className="rounded-md border-2 border-foreground/70 p-2.5 flex items-start gap-2.5 bg-foreground/[0.03]">
            <AiLabelIcon size={34} className="rounded-md mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-black uppercase tracking-wider leading-tight">{(meta as any).industryLabel}</p>
              <p className="text-[11px] text-foreground/80 mt-0.5">{(meta as any).industryText}</p>
            </div>
          </div>
        )}

        {/* Ingredients split (AI vs human) */}
        {aiPct != null && (
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
              <span className="flex items-center gap-1"><Bot className="h-3 w-3" /> AI {aiPct}%</span>
              <span className="flex items-center gap-1">Human {humanPct}% <Mic2 className="h-3 w-3" /></span>
            </div>
            <div className="flex h-3 w-full overflow-hidden rounded-full border border-foreground/30">
              <div className="bg-violet-500" style={{ width: `${aiPct}%` }} />
              <div className="bg-emerald-500" style={{ width: `${humanPct}%` }} />
            </div>
          </div>
        )}

        {/* Composition / copyright is human even when the recording is AI */}
        {hasAI && disclosure.compositionHuman && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 flex items-start gap-2">
            <Mic2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-500" />
            <div className="min-w-0">
              <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Composition &amp; copyright: 100% human</p>
              <p className="text-[11px] text-muted-foreground">AI generated the audio; the song (writing, lyrics, melody) is human-written.{disclosure.basedOn ? ` Based on ${disclosure.basedOn}.` : ""}</p>
            </div>
          </div>
        )}

        {/* Model */}
        {hasAI && disclosure.model && (
          <div className="flex items-start gap-2">
            <Bot className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-500" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">AI model</p>
              {disclosure.modelUrl ? (
                <a href={disclosure.modelUrl} target="_blank" rel="noopener noreferrer"
                   className="font-semibold inline-flex items-center gap-1 hover:underline break-all">
                  {disclosure.model} <ExternalLink className="h-3 w-3" />
                </a>
              ) : <p className="font-semibold break-words">{disclosure.model}</p>}
            </div>
          </div>
        )}

        {/* Influences with sources (Wikipedia-style) */}
        {disclosure.influences && disclosure.influences.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Influences &amp; sources</p>
            <ul className="space-y-1">
              {disclosure.influences.map((inf, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  {inf.type === "artist" ? <Mic2 className="h-3 w-3 flex-shrink-0" /> : <Music2 className="h-3 w-3 flex-shrink-0" />}
                  {inf.sourceUrl ? (
                    <a href={inf.sourceUrl} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1">
                      {inf.name} <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                  ) : <span>{inf.name}</span>}
                  <span className="text-[10px] text-muted-foreground">· {inf.type}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Live musicians overdubbed */}
        {disclosure.liveMusicians && disclosure.liveMusicians.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Live overdubs</p>
            <ul className="space-y-0.5">
              {disclosure.liveMusicians.map((m, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <Mic2 className="h-3 w-3 flex-shrink-0 text-emerald-500" />
                  <span className="font-medium">{m.name}</span>
                  {m.instrument && <span className="text-xs text-muted-foreground">— {m.instrument}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* AI plausibility read */}
        {disclosure.analysis?.summary && (
          <p className="text-xs italic text-muted-foreground border-l-2 border-foreground/20 pl-2">
            {disclosure.analysis.summary}
          </p>
        )}

        {/* Missing-credits warning + education */}
        {hasAI && missing.length > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 space-y-1">
            <p className="flex items-center gap-1.5 text-xs font-bold text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" /> Incomplete label
            </p>
            <p className="text-[11px] text-muted-foreground">Missing: {missing.join(", ")}.</p>
            <p className="text-[11px] text-muted-foreground">{AI_CREDITS_EDUCATION}</p>
          </div>
        )}

        {/* "Go full nerd" — process notes */}
        {disclosure.processNotes && (
          <div className="border-t border-foreground/15 pt-2">
            <button type="button" onClick={() => setNerd(v => !v)}
                    className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide hover:text-primary">
              {nerd ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Go full nerd — how it was made
            </button>
            {nerd && <p className="mt-2 text-xs whitespace-pre-wrap leading-relaxed">{disclosure.processNotes}</p>}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t-2 border-foreground/80 bg-foreground/[0.04] text-[10px] uppercase tracking-widest text-muted-foreground">
        Breakin&apos; Bread — human-to-AI music grading{trackTitle ? ` · ${trackTitle}` : ""}
      </div>
    </div>
  );
}
